/**
 * Connection Manager Component
 * Displays connected platforms with Test/Sync functionality
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { journalApiFetch } from '../lib/api';

interface Connection {
  id: string;
  platform: string;
  name: string;
  broker?: string;
  account_type: string;
  is_active: boolean;
  last_sync_at?: string;
  last_sync?: string;
  last_checked?: string;
  status?: 'connected' | 'disconnected' | 'error' | 'unknown';
  auto_sync?: boolean;
  sync_interval_minutes?: number;
  created_at: string;
}

interface ConnectionManagerProps {
  onImportTrades?: (connectionId: string, trades: any[], platform: string) => void;
}

const PLATFORM_INFO: Record<string, { icon: string; name: string; color: string }> = {
  binance: { icon: '🟡', name: 'Binance', color: 'yellow' },
  binaryfaster: { icon: '⚡', name: 'BinaryFaster', color: 'orange' },
  bybit: { icon: '🟠', name: 'Bybit', color: 'orange' },
  mt5: { icon: '📊', name: 'MetaTrader 5', color: 'blue' },
  mt4: { icon: '📈', name: 'MetaTrader 4', color: 'blue' },
  ctrader: { icon: '💜', name: 'cTrader', color: 'purple' },
  tradingview: { icon: '📺', name: 'TradingView', color: 'cyan' },
  ibkr: { icon: '🏛️', name: 'IBKR', color: 'green' },
  tradelocker: { icon: '🔐', name: 'TradeLocker', color: 'rose' },
};

export default function ConnectionManager({ onImportTrades }: ConnectionManagerProps) {
  const queryClient = useQueryClient();
  const [testingId, setTestingId] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, { success: boolean; message: string }>>({});
  const [syncResult, setSyncResult] = useState<Record<string, { success: boolean; message: string; trades?: any[] }>>({});

  // Fetch connections
  const { data: connectionsData, isLoading, error } = useQuery({
    queryKey: ['platform-connections'],
    queryFn: () => journalApiFetch('/api/connections'),
    refetchInterval: 60000, // Refresh every minute
  });

  const connections: Connection[] = connectionsData?.connections || [];

  // Test connection mutation
  const testConnection = useMutation({
    mutationFn: async (connectionId: string) => {
      const response = await journalApiFetch(`/api/connections/${connectionId}/test`);
      return { connectionId, ...response };
    },
    onMutate: (connectionId) => {
      setTestingId(connectionId);
      setTestResult(prev => ({ ...prev, [connectionId]: { success: false, message: 'Testing...' } }));
    },
    onSuccess: (result) => {
      setTestResult(prev => ({
        ...prev,
        [result.connectionId]: {
          success: result.success,
          message: result.message || (result.success ? 'Connected!' : 'Connection failed'),
        },
      }));
      setTestingId(null);
    },
    onError: (error: any, connectionId) => {
      setTestResult(prev => ({
        ...prev,
        [connectionId]: { success: false, message: error.message || 'Test failed' },
      }));
      setTestingId(null);
    },
  });

  // Sync trades mutation
  const syncTrades = useMutation({
    mutationFn: async (connectionId: string) => {
      const response = await journalApiFetch(`/api/connections/${connectionId}/sync`, { method: 'POST' });
      return { connectionId, ...response };
    },
    onMutate: (connectionId) => {
      setSyncingId(connectionId);
      setSyncResult(prev => ({ ...prev, [connectionId]: { success: false, message: 'Syncing...' } }));
    },
    onSuccess: (result) => {
      const connection = connections.find(c => c.id === result.connectionId);
      setSyncResult(prev => ({
        ...prev,
        [result.connectionId]: {
          success: result.success,
          message: result.message || `Found ${result.tradesFound || 0} trades`,
          trades: result.trades,
        },
      }));
      setSyncingId(null);

      // Trigger import callback if trades found
      if (result.success && result.trades?.length > 0 && onImportTrades && connection) {
        onImportTrades(result.connectionId, result.trades, connection.platform);
      }

      // Refresh connections list
      queryClient.invalidateQueries({ queryKey: ['platform-connections'] });
    },
    onError: (error: any, connectionId) => {
      setSyncResult(prev => ({
        ...prev,
        [connectionId]: { success: false, message: error.message || 'Sync failed' },
      }));
      setSyncingId(null);
    },
  });

  // Delete connection mutation
  const deleteConnection = useMutation({
    mutationFn: async (connectionId: string) => {
      return journalApiFetch(`/api/connections/${connectionId}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-connections'] });
    },
  });

  // Toggle Demo/Real mode mutation (BinaryFaster only)
  const toggleMode = useMutation({
    mutationFn: async ({ connectionId, newMode }: { connectionId: string; newMode: 'demo' | 'live' }) => {
      return journalApiFetch(`/api/connections/${connectionId}/toggle-mode`, {
        method: 'POST',
        body: JSON.stringify({ mode: newMode }),
      });
    },
    onMutate: ({ connectionId }) => {
      setTogglingId(connectionId);
    },
    onSuccess: (result, { connectionId }) => {
      setTogglingId(null);
      queryClient.invalidateQueries({ queryKey: ['platform-connections'] });
      if (result.success) {
        setSyncResult(prev => ({
          ...prev,
          [connectionId]: {
            success: true,
            message: `Switched to ${(result.mode || 'new').toUpperCase()} mode. Sync to fetch trades.`,
          },
        }));
      }
    },
    onError: (error: any, { connectionId }) => {
      setTogglingId(null);
      setSyncResult(prev => ({
        ...prev,
        [connectionId]: { success: false, message: `Mode switch failed: ${error.message}` },
      }));
    },
  });

  if (isLoading) {
    return (
      <div className="p-4 text-center text-gray-400">
        <div className="animate-spin inline-block w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full mb-2" />
        <p>Loading connections...</p>
      </div>
    );
  }

  if (error || !connectionsData?.connections) {
    return (
      <div className="p-4 text-center text-red-400">
        <p>Failed to load connections</p>
        <p className="text-sm text-gray-500 mt-1">{(error as any)?.message}</p>
      </div>
    );
  }

  if (connections.length === 0) {
    return (
      <div className="p-6 text-center text-gray-400">
        <div className="text-4xl mb-3">🔗</div>
        <p className="font-medium">No connections yet</p>
        <p className="text-sm mt-1">Add a trading account to sync your trades automatically</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Connected Platforms</h3>
        <span className="text-sm text-gray-400">{connections.length} connection{connections.length !== 1 ? 's' : ''}</span>
      </div>

      {connections.map((conn) => {
        const platform = PLATFORM_INFO[conn.platform] || { icon: '🔗', name: conn.platform, color: 'gray' };
        const testing = testingId === conn.id;
        const syncing = syncingId === conn.id;
        const test = testResult[conn.id];
        const sync = syncResult[conn.id];
        const lastSyncTime = conn.last_sync || conn.last_sync_at;
        const statusIcon = conn.status === 'connected' ? '🟢' : conn.status === 'disconnected' ? '🔴' : conn.status === 'error' ? '🟠' : '⚪';

        return (
          <div
            key={conn.id}
            className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/[0.07] transition-all"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <span className="text-2xl">{platform.icon}</span>
                  <span className="absolute -bottom-1 -right-1 text-xs">{statusIcon}</span>
                </div>
                <div>
                  <div className="font-medium text-white flex items-center gap-2">
                    {conn.name}
                    {conn.auto_sync && (
                      <span className="text-xs bg-cyan-500/20 text-cyan-400 px-1.5 py-0.5 rounded" title="Auto-sync enabled">
                        🔄 Auto
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-400 flex items-center gap-2 flex-wrap">
                    <span>{platform.name} • {conn.account_type}</span>
                    {conn.broker && <span>• {conn.broker}</span>}
                    {/* Demo/Real Toggle for BinaryFaster */}
                    {conn.platform === 'binaryfaster' && (
                      <button
                        onClick={() => {
                          const newMode = conn.account_type === 'demo' ? 'live' : 'demo';
                          toggleMode.mutate({ connectionId: conn.id, newMode });
                        }}
                        disabled={togglingId === conn.id}
                        className={`ml-1 px-2 py-0.5 rounded text-xs font-medium transition-all ${
                          conn.account_type === 'demo'
                            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/30'
                            : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30'
                        } ${togglingId === conn.id ? 'opacity-50 cursor-wait' : ''}`}
                        title={`Switch to ${conn.account_type === 'demo' ? 'REAL' : 'DEMO'} mode`}
                      >
                        {togglingId === conn.id ? '⏳' : conn.account_type === 'demo' ? '🎮→💰' : '💰→🎮'}
                      </button>
                    )}
                  </div>
                  {lastSyncTime && (
                    <div className="text-xs text-gray-500 mt-1">
                      Last sync: {new Date(lastSyncTime).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Test Button */}
                <button
                  onClick={() => testConnection.mutate(conn.id)}
                  disabled={testing || syncing}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50
                    ${test?.success 
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                      : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                >
                  {testing ? (
                    <span className="flex items-center gap-1">
                      <span className="animate-spin">⏳</span> Testing
                    </span>
                  ) : test?.success ? '✓ Connected' : '🔌 Test'}
                </button>

                {/* Sync Button */}
                <button
                  onClick={() => syncTrades.mutate(conn.id)}
                  disabled={testing || syncing}
                  className="px-3 py-1.5 bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 rounded-lg text-sm font-medium
                            hover:bg-cyan-500/30 transition-all disabled:opacity-50"
                >
                  {syncing ? (
                    <span className="flex items-center gap-1">
                      <span className="animate-spin">🔄</span> Syncing
                    </span>
                  ) : '🔄 Sync'}
                </button>

                {/* Delete Button */}
                <button
                  onClick={() => {
                    if (confirm('Delete this connection?')) {
                      deleteConnection.mutate(conn.id);
                    }
                  }}
                  className="p-1.5 text-gray-400 hover:text-red-400 transition-colors"
                  title="Delete connection"
                >
                  🗑️
                </button>
              </div>
            </div>

            {/* Result Messages */}
            {(test || sync) && (
              <div className="mt-3 pt-3 border-t border-white/10">
                {test && !testing && (
                  <div className={`text-sm ${test.success ? 'text-emerald-400' : 'text-red-400'}`}>
                    {test.success ? '✅' : '❌'} {test.message}
                  </div>
                )}
                {sync && !syncing && (
                  <div className={`text-sm ${sync.success ? 'text-cyan-400' : 'text-red-400'}`}>
                    {sync.success ? '✅' : '❌'} {sync.message}
                    {sync.trades && sync.trades.length > 0 && (
                      <span className="ml-2 text-xs text-gray-400">
                        ({sync.trades.length} trades ready to import)
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
